/**
 * Type-safe query key factories for TanStack Query
 *
 * Provides centralized query key generation to ensure consistency
 * and type safety across the application.
 *
 * Usage:
 *   queryKeys.videos.detail('123') → ['videos', 'detail', '123']
 *   queryKeys.videos.list({ page: 1 }) → ['videos', 'list', { page: 1 }]
 */

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    me: () => [...queryKeys.auth.user(), 'me'] as const,
  },
  support: {
    all: ['support'] as const,
    crispIdentity: () => [...queryKeys.support.all, 'crispIdentity'] as const,
  },
  videos: {
    all: ['videos'] as const,
    lists: () => [...queryKeys.videos.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.videos.lists(), filters] as const,
    mapList: (filters?: Record<string, unknown>) =>
      [...queryKeys.videos.all, 'map', filters] as const,
    mapListInfinite: (filters?: Record<string, unknown>) =>
      [...queryKeys.videos.all, 'mapInfinite', filters] as const,
    detail: (id: string | number) => [...queryKeys.videos.all, 'detail', String(id)] as const,
    related: (id: string | number) => [...queryKeys.videos.detail(id), 'related'] as const,
    byTagsInfinite: (tagIds: number[]) =>
      [...queryKeys.videos.all, 'byTagsInfinite', tagIds.sort().join(',')] as const,
    byCreatorInfinite: (creatorId: number | null | undefined) =>
      [...queryKeys.videos.all, 'byCreatorInfinite', creatorId ?? null] as const,
    recentInfinite: () => [...queryKeys.videos.all, 'recentInfinite'] as const,
    comments: (id: string | number) => [...queryKeys.videos.detail(id), 'comments'] as const,
    bookmarked: () => [...queryKeys.videos.all, 'bookmarked'] as const,
    bookmarkOverrides: () => [...queryKeys.videos.all, 'bookmarkOverrides'] as const,
    history: () => [...queryKeys.videos.all, 'history'] as const,
    liked: () => [...queryKeys.videos.all, 'liked'] as const,
  },
  shorts: {
    all: ['shorts'] as const,
    lists: () => [...queryKeys.shorts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.shorts.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.shorts.all, 'detail', id] as const,
    comments: (id: string) => [...queryKeys.shorts.detail(id), 'comments'] as const,
  },
  series: {
    all: ['series'] as const,
    lists: () => [...queryKeys.series.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.series.lists(), filters] as const,
    detail: (id: string | number) => [...queryKeys.series.all, 'detail', String(id)] as const,
    videos: (id: string | number) => [...queryKeys.series.detail(id), 'videos'] as const,
    trailer: (id: string | number) => [...queryKeys.series.detail(id), 'trailer'] as const,
    structure: (uuid: string) => [...queryKeys.series.all, 'structure', uuid] as const,
    watchlist: (type?: 'series' | 'course') =>
      [...queryKeys.series.all, 'watchlist', type ?? 'all'] as const,
  },
  courses: {
    all: ['courses'] as const,
    lists: () => [...queryKeys.courses.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.courses.lists(), filters] as const,
    detail: (id: string | number) => [...queryKeys.courses.all, 'detail', String(id)] as const,
    videos: (id: string | number) => [...queryKeys.courses.detail(id), 'videos'] as const,
    trailer: (id: string | number) => [...queryKeys.courses.detail(id), 'trailer'] as const,
    structure: (id: string | number) =>
      [...queryKeys.courses.all, 'structure', String(id)] as const,
    attachments: (uuid: string) => [...queryKeys.courses.all, 'attachments', uuid] as const,
    catalog: () => [...queryKeys.courses.all, 'catalog'] as const,
    bundles: () => [...queryKeys.courses.all, 'bundles'] as const,
    bundleDetail: (id: string | number) =>
      [...queryKeys.courses.all, 'bundle', String(id)] as const,
  },
  creators: {
    all: ['creators'] as const,
    lists: () => [...queryKeys.creators.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.creators.lists(), filters] as const,
    detail: (id: string | number) => [...queryKeys.creators.all, 'detail', String(id)] as const,
    videos: (id: string | number) => [...queryKeys.creators.detail(id), 'videos'] as const,
    shorts: (id: string | number) => [...queryKeys.creators.detail(id), 'shorts'] as const,
    series: (id: string | number) => [...queryKeys.creators.detail(id), 'series'] as const,
    posts: (id: string | number) => [...queryKeys.creators.detail(id), 'posts'] as const,
    courses: (id: string | number) => [...queryKeys.creators.detail(id), 'courses'] as const,
    tabs: (id: string | number) => [...queryKeys.creators.detail(id), 'tabs'] as const,
    tab: (id: string | number, tabUuid: string) =>
      [...queryKeys.creators.detail(id), 'tabs', tabUuid] as const,
  },
  community: {
    all: ['community'] as const,
    posts: () => [...queryKeys.community.all, 'posts'] as const,
    postList: (filters?: Record<string, unknown>) =>
      [...queryKeys.community.posts(), 'list', filters] as const,
    postDetail: (id: string | number) =>
      [...queryKeys.community.posts(), 'detail', String(id)] as const,
    postDetailByUuid: (uuid: string) =>
      [...queryKeys.community.posts(), 'detail', 'uuid', uuid] as const,
    comments: (postId: string | number) =>
      [...queryKeys.community.posts(), String(postId), 'comments'] as const,
  },
  subscription: {
    all: ['subscription'] as const,
    plans: () => [...queryKeys.subscription.all, 'plans'] as const,
    plansByCountry: (country?: string) =>
      [...queryKeys.subscription.plans(), 'by-country', country] as const,
    status: () => [...queryKeys.subscription.all, 'status'] as const,
    info: () => [...queryKeys.subscription.all, 'info'] as const,
    detail: () => [...queryKeys.subscription.all, 'detail'] as const,
  },
  search: {
    all: ['search'] as const,
    query: (query: string, filters?: Record<string, unknown>) =>
      [...queryKeys.search.all, query, filters] as const,
  },
  home: {
    all: ['home'] as const,
    banners: () => [...queryKeys.home.all, 'banners'] as const,
    featured: () => [...queryKeys.home.all, 'featured'] as const,
    recommended: () => [...queryKeys.home.all, 'recommended'] as const,
    shorts: () => [...queryKeys.home.all, 'shorts'] as const,
    series: () => [...queryKeys.home.all, 'series'] as const,
    courses: () => [...queryKeys.home.all, 'courses'] as const,
    creators: () => [...queryKeys.home.all, 'creators'] as const,
    data: (cursor?: string | null) => [...queryKeys.home.all, 'data', cursor] as const,
    playlists: () => [...queryKeys.home.all, 'playlists'] as const,
  },
  comments: {
    all: ['comments'] as const,
    detail: (id: number) => [...queryKeys.comments.all, 'detail', id] as const,
  },
  postComments: {
    all: ['postComments'] as const,
    detail: (id: number) => [...queryKeys.postComments.all, 'detail', id] as const,
  },
  announcements: {
    all: ['announcements'] as const,
    unread: (flags: readonly string[]) =>
      [...queryKeys.announcements.all, 'unread', flags.join(',')] as const,
    recent: (flags: readonly string[]) =>
      [...queryKeys.announcements.all, 'recent', flags.join(',')] as const,
  },
  tours: {
    all: ['tours'] as const,
    list: () => [...queryKeys.tours.all, 'list'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
  },
  dm: {
    all: ['dm'] as const,
    threads: () => [...queryKeys.dm.all, 'threads'] as const,
    messages: (threadUuid: string) => [...queryKeys.dm.all, 'messages', threadUuid] as const,
    studioThreads: () => [...queryKeys.dm.all, 'studioThreads'] as const,
  },
  coins: {
    all: ['coins'] as const,
    wallet: () => [...queryKeys.coins.all, 'wallet'] as const,
  },
  playlists: {
    all: ['playlists'] as const,
    lists: () => [...queryKeys.playlists.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.playlists.lists(), filters] as const,
    detail: (id: string | number) => [...queryKeys.playlists.all, 'detail', String(id)] as const,
  },
  studio: {
    all: ['studio'] as const,
    dashboard: () => [...queryKeys.studio.all, 'dashboard'] as const,
    videos: (params?: {
      page?: number;
      per_page?: number;
      types?: string[];
      series_ids?: number[];
      countries_ids?: number[];
      sort_by?: string;
      status?: string;
    }) => {
      // Normalize for consistent cache keys (sort arrays)
      const key = params
        ? {
            page: params.page ?? 1,
            per_page: params.per_page ?? 20,
            types: params.types?.slice().sort().join(','),
            series_ids: params.series_ids?.slice().sort().join(','),
            countries_ids: params.countries_ids?.slice().sort().join(','),
            sort_by: params.sort_by ?? 'latest',
            status: params.status,
          }
        : {};
      return [...queryKeys.studio.all, 'videos', key] as const;
    },
    shorts: (params?: {
      page?: number;
      per_page?: number;
      types?: string[];
      sort_by?: string;
      status?: string;
    }) => {
      const key = params
        ? {
            page: params.page ?? 1,
            per_page: params.per_page ?? 20,
            types: params.types?.slice().sort().join(','),
            sort_by: params.sort_by ?? 'latest',
            status: params.status,
          }
        : {};
      return [...queryKeys.studio.all, 'shorts', key] as const;
    },
    posts: (channelId?: number, page?: number) =>
      [...queryKeys.studio.all, 'posts', channelId, page] as const,
    mapStats: (creatorId: string | number) =>
      [...queryKeys.studio.all, 'mapStats', String(creatorId)] as const,
    videoDetail: (uuid: string) => [...queryKeys.studio.all, 'videoDetail', uuid] as const,
    series: {
      all: () => [...queryKeys.studio.all, 'series'] as const,
      lists: () => [...queryKeys.studio.series.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.studio.series.lists(), filters] as const,
      details: () => [...queryKeys.studio.series.all(), 'detail'] as const,
      detail: (uuid: string) => [...queryKeys.studio.series.details(), uuid] as const,
    },
    courses: {
      all: () => [...queryKeys.studio.all, 'courses'] as const,
      lists: () => [...queryKeys.studio.courses.all(), 'list'] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.studio.courses.lists(), filters] as const,
      details: () => [...queryKeys.studio.courses.all(), 'detail'] as const,
      detail: (uuid: string) => [...queryKeys.studio.courses.details(), uuid] as const,
    },
    modules: {
      all: () => [...queryKeys.studio.all, 'modules'] as const,
      lists: () => [...queryKeys.studio.modules.all(), 'list'] as const,
      list: (seriesUuid: string) => [...queryKeys.studio.modules.lists(), seriesUuid] as const,
    },
    tabs: {
      list: () => [...queryKeys.studio.all, 'tabs'] as const,
      detail: (uuid: string) => [...queryKeys.studio.all, 'tabs', uuid] as const,
    },
  },
  redeemCodes: {
    all: ['redeemCodes'] as const,
    lists: () => [...queryKeys.redeemCodes.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.redeemCodes.lists(), params] as const,
  },
  invite: {
    all: ['invite'] as const,
    myInvite: () => [...queryKeys.invite.all, 'my-invite'] as const,
  },
  creatorApplications: {
    all: ['creatorApplications'] as const,
    mine: () => [...queryKeys.creatorApplications.all, 'mine'] as const,
  },
  handler: {
    all: ['handler'] as const,
    availability: (handler: string) =>
      [...queryKeys.handler.all, 'availability', handler.toLowerCase()] as const,
  },
  users: {
    all: ['users'] as const,
    search: (query: string) => [...queryKeys.users.all, 'search', query] as const,
    byHandler: (handler: string) => [...queryKeys.users.all, 'handler', handler] as const,
  },
  clips: {
    all: ['clips'] as const,
    myClips: () => [...queryKeys.clips.all, 'my-clips'] as const,
    video: (id: number) => [...queryKeys.clips.all, 'video', id] as const,
    creator: (uuid: string) => [...queryKeys.clips.all, 'creator', uuid] as const,
  },
  shareClips: {
    all: ['shareClips'] as const,
    lists: () => [...queryKeys.shareClips.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.shareClips.lists(), filters] as const,
    detail: (slug: string) => [...queryKeys.shareClips.all, 'detail', slug] as const,
  },
  freemium: {
    all: ['freemium'] as const,
    videos: (filters?: Record<string, unknown>) => ['freemium', 'videos', filters] as const,
    videoPlay: (uuid: string) => ['freemium', 'video', uuid] as const,
  },
  publicCourses: {
    all: ['public-courses'] as const,
    catalog: (filters?: Record<string, unknown>) =>
      [...queryKeys.publicCourses.all, 'catalog', filters] as const,
    detail: (id: string | number) =>
      [...queryKeys.publicCourses.all, 'detail', String(id)] as const,
    bundleDetail: (id: string | number) =>
      [...queryKeys.publicCourses.all, 'bundle', String(id)] as const,
  },
} as const;

/**
 * Predicate matching every cache entry that can contain a community `Post`
 * in a list shape (infinite or paginated). Used by `setQueriesData` /
 * `getQueriesData` in the post reaction mutations so that like / dislike /
 * delete propagate across the community feed and every creator-profile
 * posts tab without forcing a list refetch.
 *
 * Positive matches:
 *   - ['community', 'posts', 'list', ...]                    (usePostsList — infinite)
 *   - ['creators', 'detail', <id>, 'posts', ...]             (useCreatorPosts — flat)
 *   - ['creators', 'detail', <id>, 'posts', 'infinite', ...] (useCreatorPostsInfinite)
 *
 * Negative (must NOT match):
 *   - ['studio', 'posts', ...]                 (different response shape — `body` not `caption`)
 *   - ['community', 'posts', 'detail', ...]    (single-post detail cache)
 *   - ['community', 'posts', <id>, 'comments'] (comments list)
 *   - Any other unrelated key
 */
export function isPostListQueryKey(key: readonly unknown[]): boolean {
  if (key[0] === 'community' && key[1] === 'posts' && key[2] === 'list') {
    return true;
  }
  if (key[0] === 'creators' && key[1] === 'detail' && key[3] === 'posts') {
    return true;
  }
  return false;
}
